/* eslint-disable no-console */
import * as Ably from 'ably';
import { axiosHelper } from '@setvi/shared/services';
import { AxiosMethods } from '@setvi/shared/enums';

/**
 * Browser-side realtime transport.
 *
 * ONE Ably.Realtime connection PER RESOURCE (rfq:{id} / quote:{id}), not one
 * shared connection for the whole tab. Each connection mints a single-resource
 * capability token ONCE at connect and is NEVER re-authorized.
 *
 * Why per-resource instead of a shared connection with widen/narrow authorize:
 * a shared connection re-`authorize()`-s as resources are added/removed, and
 * every authorize re-evaluates EVERY attached channel against the new token. A
 * lagging "narrow" (from releasing a resource you navigated away from, or the
 * background file-progress manager switching RFQs) would re-mint a token that no
 * longer covers a DIFFERENT resource's freshly-attached `::$space` channel, and
 * Ably denies it (40160) and pushes it to the terminal `failed` state — silently
 * breaking presence/collab until reload. Scoping a connection to a single
 * resource removes the union token entirely: no narrow, no cross-resource
 * re-evaluation, no orphaned-channel denial. A background RFQ and the currently
 * open quote simply live on separate connections and cannot interfere.
 *
 * The browser never sees the Ably root API key. Each connection hits
 * `/v2/realtime/token` with its existing JWT and gets a capability token
 * covering exactly its one channel — the server tenant-checks it before signing.
 *
 * Refcounted per resource key: ensureResource increments, releaseResource
 * decrements, and at refCount 0 the connection is closed after a short idle
 * grace period (so a React Strict Mode unmount/remount, or a fast
 * navigate-away-and-back, reuses the live connection instead of churning it).
 * Multiple consumers share one key — CollabSpacesProvider + useRfqLiveUpdates +
 * useRfqInternalNotesRealtime + useRfqFilesRealtime + the file-progress
 * AblyManager all hold rfq:{id} and ride its one connection.
 */

const TOKEN_ENDPOINT = '/v2/realtime/token';

// Idle grace before a refCount-0 connection is actually closed. Long enough to
// survive React Strict Mode's mount/unmount/mount and a quick back-then-reopen,
// short enough that a real navigation frees the socket promptly.
const IDLE_CLOSE_MS = 3000;

export type RealtimeResourceType = 'rfq' | 'quote';

export interface RealtimeResource {
  type: RealtimeResourceType;
  id: string;
}

interface ChannelDescriptor {
  resourceType: RealtimeResourceType;
  resourceId: string;
}

interface TokenResponse {
  tokenRequest: Ably.TokenRequest;
  channels: string[];
}

interface ConnectionEntry {
  resource: RealtimeResource;
  client: Ably.Realtime;
  refCount: number;
  // Resolves once the connection is `connected` (its single-resource token is
  // therefore live). Consumers await this before attaching a channel.
  ready: Promise<void>;
  // Pending idle-close timer; cleared when a consumer re-acquires the key.
  closeTimer: ReturnType<typeof setTimeout> | null;
}

// One connection per resource key. The key is the only channel a connection's
// token covers, so there is never a stale-snapshot problem across navigation.
const connections = new Map<string, ConnectionEntry>();

// Server-resolved channel names, keyed by `channelKey(resource)`. The token
// endpoint is the source of truth for the channel a resource maps to — a quote
// linked to an RFQ resolves to `rfq:{rfqId}`, etc. — so we read this rather than
// rebuilding the name client-side. Callers fall back to channelKey() until it's
// populated.
const resolvedChannels = new Map<string, string>();

function channelKey(resource: RealtimeResource): string {
  return `${resource.type}:${resource.id}`;
}

// The @ably/spaces Members layer calls presence.get() while attaching, which is
// triggered on every mount/unmount of a presence consumer (e.g. switching the
// Quotes V2 Product Match <-> Quote tabs). If a connection is transiently
// closing/closed at that instant, Ably rejects with "Connection closed". The
// rejection originates inside the SDK's own async generator — there is no call
// site of ours to await/catch — so it surfaces as an unhandled rejection AND
// the SDK/Datadog also push it to the console. It is benign: the Space
// re-attaches on the next connection cycle. Swallow only this exact signature,
// on every path it can reach the console; everything else is untouched.

/** True only for the Ably "Connection closed" error, by message or code 80017. */
function isConnectionClosed(value: unknown): boolean {
  if (!value) return false;
  if (typeof value === 'string') return value.includes('Connection closed');
  const err = value as { message?: unknown; code?: unknown };
  if (err.code === 80017) return true;
  return (
    typeof err.message === 'string' && err.message.includes('Connection closed')
  );
}

let connectionClosedSuppressed = false;
function installConnectionClosedSuppression(): void {
  if (connectionClosedSuppressed || typeof window === 'undefined') return;
  connectionClosedSuppressed = true;

  // Unhandled rejection path (browser default logging + Datadog RUM).
  window.addEventListener('unhandledrejection', event => {
    if (isConnectionClosed(event?.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  });

  // Direct console path (the SDK / Ably logger printing the error object).
  const patch = (method: 'error' | 'warn') => {
    const original = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
      if (args.some(isConnectionClosed)) return;
      original(...args);
    };
  };
  patch('error');
  patch('warn');
}
installConnectionClosedSuppression();

async function fetchTokenRequest(
  resource: RealtimeResource
): Promise<Ably.TokenRequest> {
  const key = channelKey(resource);
  // Each connection requests exactly ONE channel — its own resource.
  const channels: ChannelDescriptor[] = [
    { resourceType: resource.type, resourceId: resource.id }
  ];
  const response = await axiosHelper({
    endpoint: TOKEN_ENDPOINT,
    method: AxiosMethods.POST,
    data: { channels },
    baseURL: process.env.REVOPS_API
  });
  const payload = (response?.Data || response) as TokenResponse;
  if (!payload?.tokenRequest) {
    throw new Error('Realtime token endpoint returned no tokenRequest');
  }
  // Capture the server-resolved channel name (channels[0] resolves our one
  // requested channel).
  if (Array.isArray(payload.channels) && payload.channels.length >= 1) {
    resolvedChannels.set(key, payload.channels[0]);
  } else {
    console.warn(
      'Realtime token response had no channels[]; falling back to ' +
        'client-side channel name',
      { key }
    );
  }
  return payload.tokenRequest;
}

function createConnection(resource: RealtimeResource): Ably.Realtime {
  return new Ably.Realtime({
    authCallback: async (_tokenParams, callback) => {
      try {
        // The resource is fixed per connection, so the token never drifts.
        const tokenRequest = await fetchTokenRequest(resource);
        // Ably v2 accepts a TokenRequest from authCallback and signs it itself.
        callback(null, tokenRequest);
      } catch (err: any) {
        callback(err?.message || 'Failed to fetch realtime token', null);
      }
    },
    closeOnUnload: true
  });
}

/**
 * Acquire the connection for a resource and return it once its token is live.
 * Each call increments the resource's refcount; the first call creates the
 * connection (and its single-resource token), later calls reuse it. A pending
 * idle-close from a previous release is cancelled here so a remount reuses the
 * existing connection instead of opening a new one.
 */
export async function ensureResource(
  resource: RealtimeResource
): Promise<Ably.Realtime> {
  const key = channelKey(resource);
  const existing = connections.get(key);
  if (existing) {
    existing.refCount += 1;
    if (existing.closeTimer) {
      clearTimeout(existing.closeTimer);
      existing.closeTimer = null;
    }
    // Wait until the connection's token is live before returning so a consumer
    // can attach `::$space` immediately without racing the initial connect.
    await existing.ready.catch(() => undefined);
    return existing.client;
  }

  const client = createConnection(resource);
  // A fresh connection only reaches `connected` after minting its first token
  // (for this one resource), so `connected` implies the token covers it.
  const ready = client.connection.whenState('connected').then(() => undefined);
  const entry: ConnectionEntry = {
    resource,
    client,
    refCount: 1,
    ready,
    closeTimer: null
  };
  connections.set(key, entry);
  await ready.catch(() => undefined);
  return client;
}

/**
 * Decrement the refcount for a resource. At refCount 0 the connection is closed
 * after IDLE_CLOSE_MS — deferred so a Strict Mode remount or a fast
 * navigate-away-and-back can re-acquire the same live connection (cancelling the
 * close) instead of tearing down and rebuilding the socket. Closing the whole
 * connection detaches all of its channels with it, so there is no orphaned
 * channel and no narrow-authorize to deny anything.
 */
export async function releaseResource(
  resource: RealtimeResource
): Promise<void> {
  const key = channelKey(resource);
  const entry = connections.get(key);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount > 0) return;
  if (entry.closeTimer) return; // close already scheduled

  entry.closeTimer = setTimeout(() => {
    // Re-check under the timer: a consumer may have re-acquired the key, or the
    // entry may have been replaced/disposed.
    const current = connections.get(key);
    if (!current || current !== entry) return;
    if (current.refCount > 0) {
      current.closeTimer = null;
      return;
    }
    connections.delete(key);
    resolvedChannels.delete(key);
    try {
      current.client.close();
    } catch (err) {
      console.warn('Ably close failed', err);
    }
  }, IDLE_CLOSE_MS);
}

/**
 * The base channel name for a resource. Prefers the server-resolved name from
 * the token mint (the server is the auth boundary) and falls back to the
 * client-side `type:id` until that's populated.
 */
export function ablyChannelFor(resource: RealtimeResource): string {
  return resolvedChannels.get(channelKey(resource)) ?? channelKey(resource);
}

/**
 * The Ably Spaces channel for a resource (presence / locations / locks ride
 * here). Browsers are granted `publish` on it — Spaces' own `locations.update`
 * publishes a message — so it's the reliable channel for client-to-client
 * collaboration events. The base `rfq:{id}` channel is subscribe-only for
 * browsers (only the backend publishes file-progress there), which is why
 * publishing collab events on the base channel silently fails.
 */
export function ablySpaceChannelFor(resource: RealtimeResource): string {
  return `${ablyChannelFor(resource)}::$space`;
}

/**
 * Publish a collaboration event on a resource's Spaces channel using that
 * resource's connection, without touching the refcount. No-op if the connection
 * isn't open yet. Other clients subscribe to the same channel/event and react.
 * If the token somehow lacks `publish`, the rejection is logged (the editor's
 * own update is unaffected).
 */
export async function publishCollabEvent(
  resource: RealtimeResource,
  eventName: string,
  data: unknown
): Promise<void> {
  const entry = connections.get(channelKey(resource));
  if (!entry) return;
  try {
    const channel = entry.client.channels.get(ablySpaceChannelFor(resource));
    await channel.publish(eventName, data);
  } catch (err) {
    console.warn('Ably publish failed', { eventName, err });
  }
}

export async function disposeAblyClient(): Promise<void> {
  connections.forEach(entry => {
    if (entry.closeTimer) clearTimeout(entry.closeTimer);
    try {
      entry.client.close();
    } catch (err) {
      console.warn('Ably close failed', err);
    }
  });
  connections.clear();
  resolvedChannels.clear();
}
